import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2, Users, UserCheck, Mail } from 'lucide-react'
import { usePartySearch } from '../../hooks/usePartySearch'
import type { PartySearchResult } from '../../hooks/usePartySearch'

export interface PartyInput {
  actorId?: string
  email?: string
  label: string
  tagName: string
  role: 'CUSTOMER' | 'COUNTERPARTY' | 'TEAM'
}

interface PartyRowProps {
  party: PartyInput
  index: number
  actorId?: string
  existingTags: string[]
  onChange: (index: number, party: PartyInput) => void
  onRemove: (index: number) => void
  canRemove: boolean
}

const TAG_REGEX = /^[a-zA-Z0-9_-]{1,15}$/
const RESERVED_TAGS = ['intern', 'alle']

/** Check if a string looks like an email address */
function isEmailLike(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
}

/**
 * Derive a tag suggestion from a label.
 *
 * Handles full labels (not just the first word), Unicode diacritics,
 * and Norwegian-specific characters. If the result collides with another
 * party's tag, suffixes -2, -3, etc.
 *
 * "Acme Corp" → "acme-corp"
 * "Møller AS" → "moller-as"
 */
function deriveTag(label: string, existingTags: string[] = [], currentTag = ''): string {
  // NFKD-decompose, strip combining marks, then map characters that don't
  // decompose (ø, æ, ß).
  let s = label.normalize('NFKD').replace(/[̀-ͯ]/g, '')
  s = s.replace(/ø/gi, 'o').replace(/æ/gi, 'ae').replace(/ß/g, 'ss')
  s = s
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 13) // leave room for -NN suffix (cap is 15)
  if (!s) return ''
  const taken = new Set(
    existingTags
      .map((t) => t.toLowerCase())
      .filter((t) => t && t !== currentTag.toLowerCase()),
  )
  if (!taken.has(s)) return s
  for (let i = 2; i < 100; i++) {
    const candidate = `${s}-${i}`.slice(0, 15)
    if (!taken.has(candidate)) return candidate
  }
  return s
}

export function PartyRow({ party, index, actorId, existingTags, onChange, onRemove, canRemove }: PartyRowProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [tagEdited, setTagEdited] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const { data: searchResults } = usePartySearch(searchQuery, actorId)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelectParty = useCallback((result: PartySearchResult) => {
    const newLabel = result.display_name
    const newTag = tagEdited
      ? party.tagName
      : deriveTag(newLabel, existingTags, party.tagName)
    onChange(index, {
      ...party,
      actorId: result.koblink_id,
      email: undefined,
      label: newLabel,
      tagName: newTag,
    })
    setSearchQuery('')
    setShowResults(false)
  }, [index, party, tagEdited, existingTags, onChange])

  const handleInviteByEmail = useCallback((email: string) => {
    const localPart = email.split('@')[0] || ''
    const newTag = tagEdited
      ? party.tagName
      : deriveTag(party.label || localPart, existingTags, party.tagName)
    onChange(index, {
      ...party,
      actorId: undefined,
      email,
      label: party.label || localPart,
      tagName: newTag,
    })
    setSearchQuery('')
    setShowResults(false)
  }, [index, party, tagEdited, existingTags, onChange])

  const handleLabelChange = useCallback((label: string) => {
    const newTag = tagEdited
      ? party.tagName
      : deriveTag(label, existingTags, party.tagName)
    onChange(index, { ...party, label, tagName: newTag })
    setSearchQuery(label)
    if (label.length >= 2) setShowResults(true)
  }, [index, party, tagEdited, existingTags, onChange])

  const handleTagChange = useCallback((tagName: string) => {
    setTagEdited(true)
    onChange(index, { ...party, tagName: tagName.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 15) })
  }, [index, party, onChange])

  const ROLE_CYCLE: PartyInput['role'][] = ['COUNTERPARTY', 'CUSTOMER', 'TEAM']

  const handleRoleToggle = useCallback(() => {
    const currentIdx = ROLE_CYCLE.indexOf(party.role)
    const nextIdx = (currentIdx + 1) % ROLE_CYCLE.length
    onChange(index, {
      ...party,
      role: ROLE_CYCLE[nextIdx],
    })
  }, [index, party, onChange])

  // Validation
  const tagLower = party.tagName.toLowerCase()
  const tagError = party.tagName && !TAG_REGEX.test(party.tagName)
    ? t('negotiate.validation.tagInvalid')
    : RESERVED_TAGS.includes(tagLower)
      ? t('negotiate.validation.tagReserved', { tag: party.tagName })
      : existingTags.filter((t) => t.toLowerCase() === tagLower).length > 1
        ? t('negotiate.validation.tagDuplicate')
        : null

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-2.5 space-y-2">
      {/* Row 1: Party search / name */}
      <div className="relative" ref={searchRef}>
        <input
          type="text"
          value={party.label || searchQuery}
          onChange={(e) => {
            if (party.actorId) {
              // Already selected — edit label directly
              onChange(index, { ...party, label: e.target.value })
            } else {
              handleLabelChange(e.target.value)
            }
          }}
          onBlur={() => {
            // Auto-detect email: if label looks like an email and no actor selected, set email field
            const val = (party.label || searchQuery).trim()
            if (!party.actorId && !party.email && isEmailLike(val)) {
              handleInviteByEmail(val)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              const val = (party.label || searchQuery).trim()
              if (!party.actorId && !party.email && isEmailLike(val)) {
                handleInviteByEmail(val)
              }
            }
          }}
          placeholder={t('negotiate.partyLabelPlaceholder')}
          className="w-full px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        {/* Status icon: selected actor or email invite */}
        {party.actorId && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-success-500" title={t('negotiate.partySelected')}>
            <UserCheck size={12} />
          </span>
        )}
        {!party.actorId && party.email && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-primary-500" title={t('negotiate.invitedViaEmail')}>
            <Mail size={12} />
          </span>
        )}

        {/* Search results dropdown */}
        {showResults && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-40 overflow-y-auto">
            {searchResults && searchResults.length > 0 ? (
              searchResults.map((result) => (
                <button
                  key={result.koblink_id}
                  onClick={() => handleSelectParty(result)}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-700 flex items-center gap-1.5"
                >
                  <span className="truncate font-medium">{result.display_name}</span>
                  {result.email && (
                    <span className="text-neutral-400 truncate text-[10px]">{result.email}</span>
                  )}
                </button>
              ))
            ) : searchQuery.length >= 2 ? (
              isEmailLike(searchQuery) ? (
                <button
                  onClick={() => handleInviteByEmail(searchQuery)}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-700 flex items-center gap-1.5 text-primary-600 dark:text-primary-400"
                >
                  <Mail size={12} />
                  <span>{t('negotiate.inviteByEmail', { email: searchQuery })}</span>
                </button>
              ) : (
                <p className="px-2 py-1.5 text-xs text-neutral-400">
                  {t('negotiate.noResultsTypeEmail')}
                </p>
              )
            ) : null}
          </div>
        )}
      </div>

      {/* Row 2: Tag + Role + Remove */}
      <div className="flex items-center gap-1.5">
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <span className="text-neutral-400 text-xs">#</span>
            <input
              type="text"
              value={party.tagName}
              onChange={(e) => handleTagChange(e.target.value)}
              placeholder={t('negotiate.partyTagPlaceholder')}
              title={t('tooltips.hashTag')}
              className={`flex-1 px-1.5 py-1 text-xs border rounded bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                tagError
                  ? 'border-error-400 dark:border-error-500'
                  : 'border-neutral-200 dark:border-neutral-600'
              }`}
            />
          </div>
          {tagError && (
            <p className="text-[11px] text-error-500 mt-0.5 ml-3">{tagError}</p>
          )}
        </div>

        <button
          onClick={handleRoleToggle}
          title={t(`negotiate.role${party.role === 'COUNTERPARTY' ? 'Counterparty' : party.role === 'CUSTOMER' ? 'Customer' : 'Team'}`)}
          className={`flex items-center gap-0.5 px-1.5 py-1 text-[10px] rounded border transition-colors ${
            party.role === 'TEAM'
              ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-600 dark:bg-primary-900/30 dark:text-primary-300'
              : party.role === 'CUSTOMER'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'border-neutral-200 bg-neutral-50 text-neutral-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
          }`}
        >
          <Users size={12} />
          {party.role === 'TEAM'
            ? t('negotiate.roleTeam')
            : party.role === 'CUSTOMER'
              ? t('negotiate.roleCustomer')
              : t('negotiate.roleCounterparty')}
        </button>

        {canRemove && (
          <button
            onClick={() => onRemove(index)}
            title={t('negotiate.removeParty')}
            className="p-1 text-neutral-400 hover:text-error-500 transition-colors"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  )
}
